import { useDispatch } from 'react-redux';
import fm from 'format-message';
import { styled } from '@activebrands/core-web/libs/styletron';
import getSelector from '@activebrands/core-web/utils/get-selector';
import { getFocusRingStyles } from '@activebrands/core-web/utils/style-functions/get-focus-styles';
import { removeBasketItem, updateBasketLineQuantity } from '@activebrands/core-web/state/basket/actions';
import { useTypedSelector } from '@activebrands/core-web/state/store';
import RemoveIcon from 'components/icons/RemoveIcon';
import AmountIncrementer from 'components/inputs/AmountIncrementer';
import Paragraph from 'components/text/Paragraph';

export const Wrapper = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
});
const Remove = styled('button', {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: 'var(--color-text-subtle)',
    ...getFocusRingStyles(),
});

interface BasketCardEndProps {
    $style?: object;
    line: string;
    name?: string;
    quantity: number;
    selector?: string;
    setIsLoading: (isLoading: boolean) => void;
    size?: string;
}

const BasketCardEnd = ({ $style, line, name, quantity, selector, setIsLoading, size }: BasketCardEndProps) => {
    const dispatch = useDispatch();
    const items = useTypedSelector(state => state.basket.items);
    const product: any = items.find(item => item.line === line);

    const trackingMeta = {
        brandName: product.brandName,
        ean: product.ean,
        item: product.id,
        productName: product.productName,
        priceAsNumber: product.priceEachAsNumber,
        quantity: quantity,
        sku: product.sku,
        size: product.size,
        parentId: product.parentId,
        silkVariant: product.variationId,
    };

    const updateQuantity = quantity => {
        setIsLoading(true);
        dispatch(updateBasketLineQuantity(line, trackingMeta, quantity, setIsLoading));
    };

    const removeItem = () => {
        setIsLoading(true);
        dispatch(removeBasketItem(line, trackingMeta, quantity));
    };

    const productLabel = name && size ? `${name}, size ${size}` : name;

    return (
        <Wrapper $style={{ alignItems: 'flex-end', ...$style }}>
            <Remove
                data-selector={getSelector(selector, 'remove')}
                type="button"
                aria-label={fm('Remove {product} from cart', { product: productLabel })}
                onClick={removeItem}
            >
                <Paragraph fontKeys="Miscellaneous/12_100" aria-hidden="true">
                    {fm('Remove')}
                </Paragraph>
                <RemoveIcon width="14px" />
            </Remove>
            <AmountIncrementer
                selector={getSelector(selector, 'qty-inc')}
                count={quantity}
                productName={productLabel}
                onChange={quantity => updateQuantity(quantity)}
            />
        </Wrapper>
    );
};

export default BasketCardEnd;
